import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/auth.css';

const EditProfilePage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    streetAddress: '',
    city: '',
    postalCode: '',
    country: '',
    phoneNumber: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/user`, {
          credentials: 'include'
        });

        if (!response.ok) {
          if (response.status === 401) handleUnauthorized();
          return;
        }

        const userData = await response.json();
        updateFormState(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setMessage('Failed to load user data');
      }
    };

    fetchUserData();
  }, [API_BASE_URL, navigate]);

  const updateFormState = (userData) => {
    setFormData({
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      username: userData.username,
      streetAddress: userData.address?.streetAddress || '',
      city: userData.address?.city || '',
      postalCode: userData.address?.postalCode || '',
      country: userData.address?.country || '',
      phoneNumber: userData.phoneNumber
    });
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear related password errors when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

const validatePassword = () => {
  const errors = {};

  if (!passwordData.currentPassword) {
    errors.currentPassword = 'Current password is required';
  }

  if (!passwordData.newPassword) {
    errors.newPassword = 'New password is required';
  } else if (passwordData.newPassword.length < 6) {
    errors.newPassword = 'Password must be at least 6 characters long';
  } else if (passwordData.newPassword === passwordData.currentPassword) {
    errors.newPassword = 'New password must be different from current password';
  }

  if (!passwordData.confirmPassword) {
    errors.confirmPassword = 'Please confirm your new password';
  } else if (passwordData.newPassword !== passwordData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  setPasswordErrors(errors);
  return Object.keys(errors).length === 0;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          username: formData.username,
          address: {
            streetAddress: formData.streetAddress,
            city: formData.city,
            postalCode: formData.postalCode,
            country: formData.country
          },
          phoneNumber: formData.phoneNumber
        })
      });

      const contentType = response.headers.get('content-type');
      const responseBody = contentType?.includes('application/json')
        ? await response.json()
        : {};

      if (response.ok) {
        await handleUpdateSuccess(responseBody);
      } else {
        await handleUpdateError(response, responseBody);
      }
    } catch (error) {
      setMessage('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const contentType = response.headers.get('content-type');
      const responseBody = contentType?.includes('application/json')
        ? await response.json()
        : {};

      if (response.ok) {
        // Clear password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordSection(false);

        // Clear session and redirect to login
        document.cookie = 'token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        localStorage.removeItem('user');

        setMessage('Lozinka promenjena uspešno, šaljemo  vas na Login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        // Handle specific password change errors
        if (response.status === 400 && responseBody.message) {
          if (responseBody.message.includes('current password')) {
            setPasswordErrors({ currentPassword: 'Pogrešna lozinka' });
          } else {
            setMessage(responseBody.message);
          }
        } else if (response.status === 401) {
          handleUnauthorized();
        } else {
          setMessage(responseBody.message || 'Neuspešna promena lozinke');
        }
      }
    } catch (error) {
      setMessage('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSuccess = async (responseBody) => {
    // Check for new token in response
    if (responseBody.newToken) {
      document.cookie = `token=${responseBody.newToken}; Path=/; Secure; SameSite=Lax; Max-Age=604800`;
    }

    // Refresh user data
    try {
      const userResponse = await fetch(`${API_BASE_URL}/auth/user`, {
        credentials: 'include'
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        updateFormState(userData);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }

    setMessage('Profile updated successfully!');
  };

  const handleUpdateError = async (response, responseBody) => {
    if (response.status === 401) {
      const sessionValid = await verifySession();
      if (!sessionValid) {
        handleUnauthorized();
        return;
      }
    }
    setMessage(responseBody.message || `Update failed: ${response.statusText}`);
  };

  const verifySession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user`, {
        credentials: 'include'
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const handleUnauthorized = () => {
    document.cookie = 'token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    localStorage.removeItem('user');
    setMessage('Session expired. Redirecting to login...');
    setTimeout(() => navigate('/login'), 2000);
  };

  const togglePasswordSection = () => {
    setShowPasswordSection(!showPasswordSection);
    if (!showPasswordSection) {
      // Clear password form when opening
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordErrors({});
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Uredi profil</h1>
        {message && (
          <div className={`message ${message.includes('failed') || message.includes('error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Profile Information Form */}
        <form onSubmit={handleSubmit}>          Ime
          <input
            name="firstName"
            type="text"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          Prezime
          <input
            name="lastName"
            type="text"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
          E-mail
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          Korisničko ime
          <input
            name="username"
            type="text"
            placeholder="Username"
            value={formData.username}
            onChange={handleChange}
            required
          />
          Adresa
          <input
            name="streetAddress"
            type="text"
            placeholder="Street Address"
            value={formData.streetAddress}
            onChange={handleChange}
            required
          />
          Grad
          <input
            name="city"
            type="text"
            placeholder="City"
            value={formData.city}
            onChange={handleChange}
            required
          />
          Poštanski broj
          <input
            name="postalCode"
            type="text"
            placeholder="Postal Code"
            value={formData.postalCode}
            onChange={handleChange}
            required
          />
          Zemlja
          <input
            name="country"
            type="text"
            placeholder="Country"
            value={formData.country}
            onChange={handleChange}
            required
          />
          Broj telefona
          <input
            name="phoneNumber"
            type="text"
            placeholder="Phone Number"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Čuvanje...' : 'Sačuvaj promene'}
          </button>
        </form>

        {/* Password Change Section */}
        <div className="password-section">
          <button
            type="button"
            onClick={togglePasswordSection}
            className="toggle-password-btn"
          >
            {showPasswordSection ? 'Otkaži promenu lozinke' : 'Promeni lozinku'}
          </button>

          {showPasswordSection && (
            <form onSubmit={handlePasswordSubmit}>
              <h2>Promeni lozinku</h2>
              <div className="password-field">
                <input
                  name="currentPassword"
                  type="password"
                  placeholder="Trenutna lozinka"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
                {passwordErrors.currentPassword && (
                  <span className="field-error">{passwordErrors.currentPassword}</span>
                )}
              </div>

              <div className="password-field">
                <input
                  name="newPassword"
                  type="password"
                  placeholder="Nova lozinka"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                {passwordErrors.newPassword && (
                  <span className="field-error">{passwordErrors.newPassword}</span>
                )}
              </div>

              <div className="password-field">
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder="Potvrdi novu lozinku"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
                {passwordErrors.confirmPassword && (
                  <span className="field-error">{passwordErrors.confirmPassword}</span>
                )}
              </div>

              <button type="submit" disabled={loading}>
                {loading ? 'Čuvanje...' : 'Promeni lozinku'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage